/**
 * WB4.7 — Finance reversal / compensating transaction domain contract.
 *
 * SSOT: voucher-type-registry `reversal` stream type (requiresSourceVoucher · requiresApproval).
 */

import { getVoucherTypeMeta, type VoucherStreamType } from './voucher-type-registry';

export const REVERSAL_VOUCHER_STREAM_TYPE = 'reversal' as const satisfies VoucherStreamType;

export const REVERSAL_STREAM_TEMPLATE_ID = `stream_template_voucher_${REVERSAL_VOUCHER_STREAM_TYPE}`;

export type FinanceCompensationStatus = 'pending' | 'cancelled' | 'posted';

export interface ReversalCompensationRequest {
  id: string;
  tenantId: string;
  sourceVoucherId: string;
  streamType: typeof REVERSAL_VOUCHER_STREAM_TYPE;
  streamTemplateId: string;
  objectId: string;
  businessDomain: string;
  requiresApproval: true;
  status: FinanceCompensationStatus;
  createdAt: string;
  cancelledAt?: string;
  /** stream_execution id from reversal voucher posting (real compensating voucher) */
  compensatingStreamId?: string;
  /** Alias for compensatingStreamId — retained for API/revision snapshot compatibility */
  compensatingVoucherId?: string;
  postedAt?: string;
}

export interface ValidateReversalCompensationInput {
  sourceVoucherId: string;
  streamId?: string;
  streamType?: string;
}

export interface ValidateReversalCompensationResult {
  valid: boolean;
  streamType: typeof REVERSAL_VOUCHER_STREAM_TYPE;
  requiresApproval: true;
  requiresSourceVoucher: true;
  errors: string[];
}

export function isReversalStreamExecution(input: {
  streamId?: string;
  streamType?: string;
}): boolean {
  const streamId = input.streamId?.trim() ?? '';
  const streamType = input.streamType?.trim() ?? '';
  return (
    streamId === REVERSAL_STREAM_TEMPLATE_ID ||
    streamId.endsWith('_reversal') ||
    streamType === REVERSAL_VOUCHER_STREAM_TYPE
  );
}

export function parseSourceVoucherIdFromStreamContext(
  streamContext: Record<string, unknown>
): string | null {
  const direct = streamContext.sourceVoucherId;
  if (typeof direct === 'string' && direct.trim()) {
    return direct.trim();
  }
  const nested = streamContext.references;
  if (Array.isArray(nested)) {
    for (const ref of nested) {
      if (ref && typeof ref === 'object' && !Array.isArray(ref)) {
        const record = ref as Record<string, unknown>;
        if (record.key === 'sourceVoucherId' && typeof record.objectId === 'string') {
          return record.objectId.trim() || null;
        }
      }
    }
  }
  return null;
}

export function validateReversalCompensationInput(
  input: ValidateReversalCompensationInput
): ValidateReversalCompensationResult {
  const meta = getVoucherTypeMeta(REVERSAL_VOUCHER_STREAM_TYPE);
  const errors: string[] = [];
  const sourceVoucherId = input.sourceVoucherId.trim();

  if (!sourceVoucherId) {
    errors.push('sourceVoucherId is required for reversal compensation');
  }

  if (input.streamId && !isReversalStreamExecution({ streamId: input.streamId })) {
    errors.push(`streamId must reference reversal template (${REVERSAL_STREAM_TEMPLATE_ID})`);
  }

  if (input.streamType && input.streamType !== REVERSAL_VOUCHER_STREAM_TYPE) {
    errors.push(`streamType must be ${REVERSAL_VOUCHER_STREAM_TYPE}`);
  }

  if (!meta.requiresSourceVoucher) {
    errors.push('reversal voucher type must require source voucher linkage');
  }

  if (!meta.requiresApproval) {
    errors.push('reversal voucher type must require approval before posting');
  }

  return {
    valid: errors.length === 0,
    streamType: REVERSAL_VOUCHER_STREAM_TYPE,
    requiresApproval: true,
    requiresSourceVoucher: true,
    errors,
  };
}

export function canCancelReversalCompensation(status: FinanceCompensationStatus): boolean {
  return status === 'pending';
}

export type ReversalCompensationRestoreAction = 'cancel_pending' | 'skip_posted';

export function hasPostedCompensatingStream(
  record: Pick<ReversalCompensationRequest, 'status' | 'compensatingStreamId'>
): boolean {
  return record.status === 'posted' && Boolean(record.compensatingStreamId?.trim());
}

export function resolveReversalCompensationRestoreAction(
  status: FinanceCompensationStatus,
  options?: { compensatingStreamId?: string }
): ReversalCompensationRestoreAction {
  if (status === 'pending' && !options?.compensatingStreamId?.trim()) {
    return 'cancel_pending';
  }
  return 'skip_posted';
}

export interface ValidateReversalCompensationPostingResult {
  valid: boolean;
  errors: string[];
}

export function validateReversalCompensationPosting(
  record: Pick<ReversalCompensationRequest, 'status' | 'sourceVoucherId' | 'requiresApproval'>
): ValidateReversalCompensationPostingResult {
  const errors: string[] = [];

  if (record.status !== 'pending') {
    errors.push(`reversal compensation must be pending to post (current: ${record.status})`);
  }

  if (!record.sourceVoucherId.trim()) {
    errors.push('sourceVoucherId is required for reversal posting');
  }

  if (!record.requiresApproval) {
    errors.push('reversal voucher type must require approval before posting');
  }

  return { valid: errors.length === 0, errors };
}

export function buildCompensatingVoucherId(input: {
  sourceVoucherId: string;
  requestId: string;
}): string {
  const suffix = input.requestId.slice(-8);
  return `comp-voucher-${input.sourceVoucherId.trim()}-${suffix}`;
}

export function applyReversalCompensationPosting(
  record: ReversalCompensationRequest,
  compensatingVoucherId: string,
  compensatingStreamId?: string
): ReversalCompensationRequest {
  const validation = validateReversalCompensationPosting(record);
  if (!validation.valid) {
    throw new Error(validation.errors.join('; '));
  }

  const voucherId = compensatingVoucherId.trim();
  if (!voucherId) {
    throw new Error('compensatingVoucherId is required for reversal posting');
  }

  const streamId = compensatingStreamId?.trim() || voucherId;

  return {
    ...record,
    status: 'posted',
    compensatingStreamId: streamId,
    compensatingVoucherId: voucherId,
    postedAt: new Date().toISOString(),
  };
}

export function buildReversalCompensationRequest(input: {
  tenantId: string;
  sourceVoucherId: string;
  objectId: string;
  businessDomain: string;
  requestId: string;
}): ReversalCompensationRequest {
  const validation = validateReversalCompensationInput({
    sourceVoucherId: input.sourceVoucherId,
    streamType: REVERSAL_VOUCHER_STREAM_TYPE,
  });
  if (!validation.valid) {
    throw new Error(validation.errors.join('; '));
  }

  return {
    id: input.requestId,
    tenantId: input.tenantId,
    sourceVoucherId: input.sourceVoucherId.trim(),
    streamType: REVERSAL_VOUCHER_STREAM_TYPE,
    streamTemplateId: REVERSAL_STREAM_TEMPLATE_ID,
    objectId: input.objectId,
    businessDomain: input.businessDomain,
    requiresApproval: true,
    status: 'pending',
    createdAt: new Date().toISOString(),
  };
}
